import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Music, Play, Pause, Volume2, VolumeX, Download, ChevronDown } from "lucide-react";

const MUSIC_URL =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_become-movement/artifacts/ey5p3fuv_Choosing%20Light.wav";

export default function MusicPlayer() {
  const audioRef = useRef(null);
  const startedRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.35);
  const [open, setOpen] = useState(false);

  // Sync volume
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // Try to start playback on the first user gesture (autoplay-with-sound is blocked otherwise)
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.loop = true;
    a.volume = volume;

    const tryStart = () => {
      if (startedRef.current) return;
      a.play()
        .then(() => {
          startedRef.current = true;
          setPlaying(true);
          remove();
        })
        .catch(() => {});
    };
    const remove = () => {
      ["pointerdown", "keydown", "touchstart", "wheel"].forEach((e) =>
        window.removeEventListener(e, tryStart)
      );
    };
    ["pointerdown", "keydown", "touchstart", "wheel"].forEach((e) =>
      window.addEventListener(e, tryStart, { passive: true })
    );
    return remove;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().then(() => { startedRef.current = true; setPlaying(true); }).catch(() => {});
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  return (
    <div className="fixed bottom-24 right-6 z-[60]" data-testid="music-player">
      <audio ref={audioRef} src={MUSIC_URL} preload="auto" loop />

      {/* Round icon button */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="music-toggle-panel"
        aria-label="Music player"
        className="relative flex h-12 w-12 items-center justify-center rounded-full bg-[#1B0812]/90 backdrop-blur-md border border-white/15 text-coral shadow-[0_10px_28px_-8px_rgba(0,0,0,0.6)] hover:scale-105 transition-transform duration-300"
      >
        {playing && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-coral/30 opacity-70" />}
        <Music size={18} />
      </button>

      {/* Compact popover */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            data-testid="music-panel"
            className="absolute bottom-full right-0 mb-3 w-56 rounded-2xl bg-[#1B0812]/95 backdrop-blur-md border border-white/15 p-4 text-cream shadow-[0_16px_40px_-10px_rgba(0,0,0,0.7)]"
          >
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="font-serif text-base leading-tight truncate">Choosing Light</p>
                <p className="font-display text-[0.55rem] uppercase tracking-[0.18em] text-cream/50">Choices &amp; Decisions</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-cream/50 hover:text-cream flex-none" aria-label="Close">
                <ChevronDown size={16} />
              </button>
            </div>

            <div className="mt-3 flex items-center gap-2.5">
              <button
                onClick={toggle}
                data-testid="music-play-pause"
                aria-label={playing ? "Pause" : "Play"}
                className="flex h-9 w-9 flex-none items-center justify-center rounded-full brand-gradient text-white transition-transform hover:scale-105"
              >
                {playing ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
              </button>

              <button
                onClick={() => setVolume((v) => (v > 0 ? 0 : 0.35))}
                data-testid="music-mute"
                aria-label={volume > 0 ? "Mute" : "Unmute"}
                className="text-cream/70 hover:text-coral transition-colors flex-none"
              >
                {volume > 0 ? <Volume2 size={16} /> : <VolumeX size={16} />}
              </button>

              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                data-testid="music-volume"
                aria-label="Volume"
                className="w-full h-1.5 cursor-pointer appearance-none rounded-full bg-white/20"
                style={{ accentColor: "#E8266F" }}
              />
            </div>

            <a
              href={MUSIC_URL}
              download="Choosing Light.wav"
              data-testid="music-download"
              className="mt-3 flex items-center justify-center gap-2 rounded-full border border-white/20 py-2 font-display text-[0.6rem] uppercase tracking-[0.16em] font-bold text-cream hover:border-coral hover:text-coral transition-colors"
            >
              <Download size={13} /> Download
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
